class CompleteYourRoutine extends HTMLElement {
  constructor() {
    super();

    this.items = [];
    this.button = null;
    this.defaultButtonText = 'Select items';
    this.selectedButtonText = 'Add selected items';
    this.loadingText = 'Adding...';
    this.addedText = 'Added';
    this.sourceId = '';

    this.handleChange = this.handleChange.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-routine-item]'));
    this.button = this.querySelector('[data-routine-button]');

    this.defaultButtonText = this.dataset.buttonDefaultText || 'Select items';
    this.selectedButtonText = this.dataset.buttonSelectedText || 'Add selected items';
    this.loadingText = this.dataset.loadingText || 'Adding...';
    this.addedText = this.dataset.addedText || 'Added';
    this.sourceId = this.dataset.blockId || 'complete-your-routine';

    this.addEventListener('change', this.handleChange);

    if (this.button) {
      this.button.addEventListener('click', this.handleClick);
    }

    this.updateButton();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);

    if (this.button) {
      this.button.removeEventListener('click', this.handleClick);
    }
  }

  handleChange(event) {
    const target = event.target;

    if (!(target instanceof HTMLElement)) return;

    if (target.matches('[data-routine-checkbox]')) {
      this.updateButton();
      return;
    }

    if (target.matches('[data-routine-variant-select]')) {
      this.updateVariant(target);
      this.updateButton();
    }
  }

  updateVariant(select) {
    const item = select.closest('[data-routine-item]');
    if (!item) return;

    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) return;

    const checkbox = item.querySelector('[data-routine-checkbox]');
    const priceElement = item.querySelector('[data-routine-price]');
    const imageElement = item.querySelector('.complete-routine__image');

    const variantId = selectedOption.value;
    const variantTitle = selectedOption.dataset.variantTitle || selectedOption.textContent.trim();
    const price = selectedOption.dataset.price;
    const available = selectedOption.dataset.available === 'true';
    const image = selectedOption.dataset.image;

    if (checkbox) {
      checkbox.dataset.variantId = variantId;
      checkbox.dataset.variantTitle = variantTitle;
      checkbox.disabled = !available;

      if (!available) {
        checkbox.checked = false;
      }
    }

    if (priceElement && price) {
      priceElement.textContent = price;
    }

    if (imageElement && image) {
      imageElement.src = image;
      imageElement.srcset = '';
    }
  }

  getSelectedItems() {
    const selectedCheckboxes = Array.from(
      this.querySelectorAll('[data-routine-checkbox]:checked:not(:disabled)')
    );

    return selectedCheckboxes
      .map((checkbox) => {
        const variantId = Number(checkbox.dataset.variantId);

        if (!variantId) return null;

        return {
          id: variantId,
          quantity: 1,
          properties: {
            '_Source': 'Complete your routine'
          }
        };
      })
      .filter(Boolean);
  }

  updateButton() {
    if (!this.button) return;

    const selectedItems = this.getSelectedItems();
    const selectedCount = selectedItems.length;

    this.button.disabled = selectedCount === 0;

    if (selectedCount === 0) {
      this.button.textContent = this.defaultButtonText;
      return;
    }

    this.button.textContent = `${this.selectedButtonText} (${selectedCount})`;
  }

  async handleClick() {
    const items = this.getSelectedItems();

    if (!items.length || !this.button || this.button.disabled) return;

    this.setLoading(true);

    try {
      const addResponse = await this.addItemsToCart(items);
      const cart = await this.getCart();

      this.dispatchCartUpdate(cart, {
        source: 'complete-your-routine',
        itemCount: cart.item_count,
        variantId: String(items[0].id),
        sections: addResponse.sections || {}
      });

      this.clearSelectedItems();
      this.setAdded();

      window.setTimeout(() => {
        this.setLoading(false);
        this.updateButton();
      }, 900);
    } catch (error) {
      this.setLoading(false);
      this.dispatchCartError(error);
    }
  }

  async addItemsToCart(items) {
    const response = await fetch(this.getRoute('cart/add.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items,
        sections: this.getSectionsToRender(),
        sections_url: window.location.pathname
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw data;
    }

    return data;
  }

  async getCart() {
    const response = await fetch(this.getRoute('cart.js'), {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw data;
    }

    return data;
  }

  getRoute(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${path}`;
  }

  getSectionsToRender() {
    const sections = new Set();

    document
      .querySelectorAll('cart-drawer-component, cart-items-component, cart-icon, [data-cart-count]')
      .forEach((element) => {
        const sectionElement = element.closest('[id^="shopify-section-"]');

        if (!sectionElement) return;

        const sectionId = sectionElement.id.replace('shopify-section-', '');

        if (sectionId) {
          sections.add(sectionId);
        }
      });

    ['cart-drawer', 'cart-icon-bubble', 'main-cart', 'header'].forEach((sectionId) => {
      if (
        document.getElementById(`shopify-section-${sectionId}`) ||
        document.getElementById(sectionId) ||
        document.querySelector(`[data-section-id="${sectionId}"]`)
      ) {
        sections.add(sectionId);
      }
    });

    return Array.from(sections).slice(0, 5);
  }

  dispatchCartUpdate(cart, data = {}) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: this.sourceId,
          data: {
            ...data
          }
        }
      })
    );
  }

  dispatchCartError(error) {
    const message =
      error?.message ||
      error?.description ||
      'Unable to add selected products to cart.';

    document.dispatchEvent(
      new CustomEvent('cart:error', {
        bubbles: true,
        detail: {
          sourceId: this.sourceId,
          data: {
            message,
            errors: error?.errors || error,
            description: error?.description || message
          }
        }
      })
    );
  }

  clearSelectedItems() {
    this.querySelectorAll('[data-routine-checkbox]:checked').forEach((checkbox) => {
      checkbox.checked = false;
    });
  }

  setLoading(isLoading) {
    if (!this.button) return;

    this.button.classList.toggle('complete-routine__button--loading', isLoading);

    if (isLoading) {
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.button.textContent = this.loadingText;
      return;
    }

    this.button.removeAttribute('aria-busy');
    this.updateButton();
  }

  setAdded() {
    if (!this.button) return;

    this.button.classList.remove('complete-routine__button--loading');
    this.button.classList.add('complete-routine__button--added');
    this.button.textContent = this.addedText;

    window.setTimeout(() => {
      this.button.classList.remove('complete-routine__button--added');
    }, 900);
  }
}

if (!customElements.get('complete-your-routine')) {
  customElements.define('complete-your-routine', CompleteYourRoutine);
}